import React from "react";
import { FormikHelpers, FormikValues } from "formik";
import { useQuery, useMutation } from "@apollo/client";
import { Alert } from "@mui/material";
import { gql } from "graphql-tag";
import { useParams } from "react-router-dom";
import { authorizeForRole } from "components/auth/authorize-for-role";
import { trackEvent } from "services/analytics";
import { ServerError, LoadingSpinner } from "components/graphql-views";
import UserForm from "pages/shared-forms/user";
import { getUserforTeam, getUserforTeamVariables, UserRole } from "types/gql-types";
import { Viewer } from "components/auth/types";
import {
  cleanUserFormOutput,
  handleSubmitError,
  handleResponse,
  UserFormData,
} from "./shared-create-update";

type Actions = FormikHelpers<FormikValues>;

const gqlUserToOption = (x: { id: string; name: string }) => ({ id: x.id, label: x.name });

const ArchivedWarning: React.FC = () => (
  <Alert severity="warning" style={{ marginBottom: "16px", maxWidth: "800px" }}>
    <strong>This user is archived</strong>
    <br />
    Archived users cannot log in to the system. You can restore them from the team
    settings page.
  </Alert>
);

const USER = gql`
  query getUserforTeam($id: ID!) {
    user(id: $id) {
      id
      organizationId
      firstName
      lastName
      email
      roles
      directPhone
      title
      isArchived
      lists {
        id
        name
      }
    }
  }
`;

const UPDATE_USER_MUTATION = gql`
  mutation updateUserForTeam($id: ID!, $data: UserInput!) {
    updateUser(id: $id, data: $data) {
      id
      firstName
      lastName
      email
      roles
    }
  }
`;
type Props = { viewer: Viewer }

const EditTeamMember: React.FC<Props> = (_props: Props) => {
  const { userId = "" } = useParams();
  const { loading, data, error } = useQuery<getUserforTeam, getUserforTeamVariables>(USER, {
    variables: { id: userId },
    fetchPolicy: "cache-and-network",
  });
  const [updateUser, updateUserMutation] =
    useMutation<{ res: { id: string } }>(UPDATE_USER_MUTATION);

  if (error) return <ServerError desc={error.message} />;
  if (loading) return <LoadingSpinner />;
  if (!data) return <ServerError desc="Missing Data" />;

  const handleSubmit = async (values: UserFormData, actions: Actions) => {
    const userInput = cleanUserFormOutput(values);
    // @ts-ignore TODO fix ids passed in from GQL
    const mutationVars = { id: values.id, data: userInput };
    // Nested objects are not supported by Pendo.
    const fromData = Object.entries(data.user).reduce((acc, [key, val]) => (
      key === "lists" ? acc : { ...acc, [`from_${key}`]: val }), {});
    const toData = Object.entries(userInput).reduce((acc, [key, val]) => (
      key === "lists" ? acc : { ...acc, [`to_${key}`]: val }), {});
    trackEvent("manager:updated_user", { ...fromData, ...toData });

    await updateUser({ variables: mutationVars })
      // clear form and go to /team page
      .then(handleResponse(actions))
      .catch(handleSubmitError("UPDATE"));
  };

  const user = {
    ...data.user,
    lists: (data.user.lists || []).map(gqlUserToOption),
  };

  return (
    <div>
      <h2 className="margin-bottom-l">Update User</h2>
      {data.user.isArchived && <ArchivedWarning />}
      <div>
        <UserForm
          isSending={updateUserMutation.loading}
          onSubmit={handleSubmit}
          // @ts-ignore
          initialData={user}
        />
      </div>
    </div>
  );
};


export default authorizeForRole<Record<string, unknown>>(
  [UserRole.SUPERVISOR],
  EditTeamMember as unknown as React.FC<Record<string, unknown>>,
);
